import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button, Card, CardActionArea, CardContent,
  TextField, Typography, Box, Grid2, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, Skeleton, Pagination, Alert, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AppShell from './components/AppShell';
import api, { BASE_URL } from './services/api';
import { useAuth } from './context/AuthContext';
import { formatRelative, formatDate } from './utils/dates';

const GREETINGS = [
  (name) => `Welcome back${name ? `, ${name}` : ''}`,
  (name) => `Good to see you${name ? `, ${name}` : ''}`,
  (name) => `Ready to go${name ? `, ${name}` : ''}`,
  (name) => `Back at it${name ? `, ${name}` : ''}`,
  (name) => `All systems go${name ? `, ${name}` : ''}`,
  (name) => `Let's make it happen${name ? `, ${name}` : ''}`,
  (name) => `Great to have you back${name ? `, ${name}` : ''}`,
];

const CARD_SX = {
  elevation: 0,
  sx: {
    border: '1px solid #e0e0e0',
    borderRadius: 3,
    transition: 'box-shadow 0.2s, transform 0.2s',
    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
  },
};

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// sortKey is the value DRF's OrderingFilter expects. survey_count is an
// annotation, so it sorts by the annotated name, not the serializer field.
const COLUMNS = [
  { id: 'name', label: 'Project', sortKey: 'name' },
  { id: 'survey_count', label: 'Surveys', sortKey: 'survey_count_annotated', align: 'right' },
  { id: 'created_by_username', label: 'Created by', sortKey: null },
  { id: 'created_at', label: 'Created', sortKey: 'created_at', align: 'right' },
];

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [greeting] = useState(() => GREETINGS[Math.floor(Math.random() * GREETINGS.length)]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDir, setOrderDir] = useState('desc');

  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [recents, setRecents] = useState([]);

  // Debounce the box so a fast typist fires one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get('/projects/', {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: search || undefined,
          ordering: `${orderDir === 'desc' ? '-' : ''}${orderBy}`,
        },
      })
      .then((res) => {
        if (cancelled) return; // a newer query already went out
        const data = res.data;
        setProjects(Array.isArray(data) ? data : data.results ?? []);
        setTotal(Array.isArray(data) ? data.length : data.count ?? 0);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Keep the real error in the console — the Alert only has room for the
        // headline, and the status is what distinguishes a dead server from a
        // permissions problem.
        console.error('GET /projects/ failed', err);
        setError(err);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [page, search, orderBy, orderDir, reloadToken]);

  const loadRecents = useCallback(() => {
    api.get('/projects/recent/')
      .then((res) => setRecents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRecents([])); // the strip is a convenience; never block the page
  }, []);

  useEffect(() => { loadRecents(); }, [loadRecents]);

  const handleSort = (sortKey) => {
    if (!sortKey) return;
    setOrderDir(orderBy === sortKey && orderDir === 'asc' ? 'desc' : 'asc');
    setOrderBy(sortKey);
    setPage(1);
  };

  const pageCount = Math.ceil(total / PAGE_SIZE);
  const searching = search.trim().length > 0;

  return (
    <AppShell>
      {/* Hero */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <img src="/logo.png" alt="Ranger" style={{ width: 90, height: 90, marginBottom: 12 }} />
        <Typography variant="h3" fontFamily="'Work Sans', sans-serif" fontWeight={700} gutterBottom>
          {greeting(user?.username)}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select a project to get started, or search below.
        </Typography>
        {user?.role === 'stack' && (
          <Button
            component={Link}
            to="/add-project"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            New Project
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ maxWidth: 480, mx: 'auto', mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search projects…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ bgcolor: 'white', borderRadius: 2 }}
        />
      </Box>

      {/* Recent — hidden while searching, since the table is the answer then */}
      {!searching && recents.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontFamily="'Work Sans', sans-serif" color="text.secondary">
              Recent
            </Typography>
          </Box>
          <Grid2 container spacing={2}>
            {recents.map((project) => (
              <Grid2 key={project.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card {...CARD_SX}>
                  <CardActionArea component={Link} to={`/project/${project.id}`}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                        <Box sx={{ bgcolor: 'primary.light', borderRadius: 2, p: 0.75, display: 'flex' }}>
                          <FolderIcon sx={{ color: 'primary.dark', fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle1" fontFamily="'Work Sans', sans-serif" fontWeight={600} noWrap>
                          {project.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {project.survey_count ?? 0} survey{project.survey_count !== 1 ? 's' : ''}
                        {' · opened '}{formatRelative(project.last_opened_at)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid2>
            ))}
          </Grid2>
        </Box>
      )}

      {/* All projects */}
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Typography variant="subtitle2" fontFamily="'Work Sans', sans-serif" color="text.secondary">
          {searching ? 'Results' : 'All projects'}
        </Typography>
        {!loading && !error && <Chip label={total} size="small" variant="outlined" />}
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => setReloadToken((t) => t + 1)}>
              Retry
            </Button>
          }
          sx={{ borderRadius: 3 }}
        >
          {error.response
            ? `Couldn't load projects — the server returned ${error.response.status}${
                error.response.status === 403 ? ' (your account lacks access)' : ''
              }.`
            : `Couldn't reach the API at ${BASE_URL}.`}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || 'left'}
                      sx={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600, bgcolor: '#fafafa' }}
                    >
                      {col.sortKey ? (
                        <TableSortLabel
                          active={orderBy === col.sortKey}
                          direction={orderBy === col.sortKey ? orderDir : 'asc'}
                          onClick={() => handleSort(col.sortKey)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Skeleton rows hold the table height steady between queries
                  // instead of collapsing to the empty state and back.
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id} align={col.align || 'left'}>
                          <Skeleton variant="text" width={col.id === 'name' ? '60%' : '40%'} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searching ? `No projects matching "${search}"` : 'No projects yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow
                      key={project.id}
                      hover
                      onClick={() => navigate(`/project/${project.id}`)}
                      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <FolderIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                          {/* A real link so the row supports middle-click and
                              keyboard focus; the row's onClick handles the rest
                              of the row, so stop it double-firing here. */}
                          <Typography
                            component={Link}
                            to={`/project/${project.id}`}
                            onClick={(e) => e.stopPropagation()}
                            variant="body2"
                            fontWeight={600}
                            sx={{ textDecoration: 'none', color: 'text.primary' }}
                          >
                            {project.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {project.survey_count ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {project.created_by_username || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(project.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                shape="rounded"
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </AppShell>
  );
};

export default Home;
